import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Bot, Save, Sparkles, MessageSquare, Database, Package, Link2, Wrench, Eye, Zap, Brain, Clock, BarChart3, FileText, Globe, Shield, Send, Settings, Edit2, Check, X, QrCode, Calendar, BookOpen, Video, Upload, Smartphone,
  Cpu, Rocket, ZapIcon, Wind, TargetIcon, BrainCircuit, CreditCard, Phone
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { agentService, productService } from '../../api/services';
import { useToast } from '../../contexts/ToastContext';
import WhatsAppPhoneConnect from '../../components/whatsapp/WhatsAppPhoneConnect';
import WhatsAppQRConnect from '../../components/whatsapp/WhatsAppQRConnect';

export default function CreateAgent() {
  const navigate = useNavigate();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('prompt');
  const [agentName, setAgentName] = useState('New Agent');
  const [isEditingName, setIsEditingName] = useState(false);
  const [promptScore] = useState(9.9);
  const [isPlayground, setIsPlayground] = useState(false);
  const [whatsappConnected, setWhatsappConnected] = useState(false);
  const [connMethod, setConnMethod] = useState('qr');
  const [showQRCode, setShowQRCode] = useState(false);
  const [createdAgent, setCreatedAgent] = useState(null);
  const [whatsappSessionId, setWhatsappSessionId] = useState(null);
  const [whatsappPhoneNumber, setWhatsappPhoneNumber] = useState(null);
  const [connectedTools, setConnectedTools] = useState({
    googleCalendar: false,
    notion: false,
    calendly: false,
  });

  const [formData, setFormData] = useState({
    model: 'GPT-4 mini',
    style: 'Normal',
    prompt: `Je suis l'assistant de [Nom de ta boutique], spécialisé(e) dans [décris ta niche ou tes produits].

Mon ton est [chaleureux / professionnel / décontracté] et je m'adresse à mes clients en [langue].

[Ajoute ici toute instruction spécifique à ta boutique : politique de retour, délais de livraison, questions fréquentes, produits phares, etc.]`,
    knowledgeBase: [],
    products: [],
    connections: {
      whatsapp: false,
      messenger: false,
      instagram: false,
      website: true,
      email: false,
    },
    temperature: 0.7,
    maxTokens: 2000,
    responseTime: 'Rapide',
    language: 'Français',
    timezone: 'Europe/Paris',
    workingHours: { start: '09:00', end: '18:00' },
    autoResponse: true,
    learningMode: true,
    tools: {
      calendar: { enabled: false, provider: 'internal' },
    },
  });

  const handleToggleGoogleCalendar = async (enabled) => {
    setConnectedTools((p) => ({ ...p, googleCalendar: enabled }));
    setFormData((f) => ({
      ...f,
      tools: { ...f.tools, calendar: { enabled, provider: 'internal' } },
    }));

    const agentId = createdAgent?.id;
    if (!agentId) {
      toast.info(
        enabled
          ? "Capacité activée — enregistrée à la sauvegarde de l'agent"
          : 'Capacité désactivée'
      );
      return;
    }

    try {
      await agentService.updateAgent(agentId, {
        tools: { calendar: { enabled, provider: 'internal' } },
      });
      toast.success(enabled ? "Google Calendar activé pour l'agent" : 'Google Calendar désactivé');
    } catch (e) {
      setConnectedTools((p) => ({ ...p, googleCalendar: !enabled }));
      toast.error('Impossible de mettre à jour la capacité calendrier');
    }
  };

  const tabs = [
    { id: 'prompt', label: 'Prompt', icon: MessageSquare },
    { id: 'connections', label: 'Connexions', icon: Link2 },
    { id: 'payment', label: 'Paiement', icon: CreditCard },
    { id: 'products', label: 'Produits et services', icon: Package },
    { id: 'knowledge', label: 'Base de connaissances', icon: Database },
    { id: 'tools', label: 'Outils', icon: Wrench },
    { id: 'settings', label: 'Paramètres', icon: Settings },
  ];

  const models = [
    {
      id: 'gpt-4-mini',
      name: 'GPT-4 mini',
      desc: 'Équilibre parfait entre vitesse et intelligence',
      icon: ZapIcon,
      performance: 85,
      speed: 'Ultra-rapide',
    },
    {
      id: 'gpt-4',
      name: 'GPT-4',
      desc: 'Puissance maximale pour tâches complexes',
      icon: BrainCircuit,
      performance: 98,
      speed: 'Rapide',
    },
    {
      id: 'gpt-3.5',
      name: 'GPT-3.5 Turbo',
      desc: 'Optimisé pour réponses instantanées',
      icon: Wind,
      performance: 75,
      speed: 'Instantané',
    },
    {
      id: 'claude',
      name: 'Claude 3',
      desc: 'Spécialiste conversations naturelles',
      icon: TargetIcon,
      performance: 92,
      speed: 'Rapide',
    },
  ];

  const communicationStyles = [
    { id: 'normal', name: 'Normal', desc: 'Ton équilibré et naturel' },
    { id: 'formal', name: 'Formel', desc: 'Professionnel et courtois' },
    { id: 'casual', name: 'Décontracté', desc: 'Amical et accessible' },
    { id: 'enthusiastic', name: 'Enthousiaste', desc: 'Énergique et motivant' },
  ];

  const [paymentNumbers, setPaymentNumbers] = useState({ orangeMoney: '', mtnMoney: '' });

  const [saving, setSaving] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');

  const [pgMessages, setPgMessages] = useState([]);
  const [pgInput, setPgInput] = useState('');
  const [pgLoading, setPgLoading] = useState(false);
  const [pgError, setPgError] = useState('');

  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [showProductForm, setShowProductForm] = useState(false);
  const [productSaving, setProductSaving] = useState(false);
  const [productFormError, setProductFormError] = useState('');
  const [newProduct, setNewProduct] = useState({ name: '', category: '', price: '', stock: '', shortDescription: '' });
  const [newImages, setNewImages] = useState([]);

  const fileToDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleProductImageSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    const tooBig = files.find((f) => f.size > 2 * 1024 * 1024);
    if (tooBig) {
      setProductFormError(`"${tooBig.name}" dépasse 2 Mo. Choisissez une image plus légère.`);
      return;
    }
    try {
      const loaded = await Promise.all(
        files.map(async (f) => ({ id: `${Date.now()}-${f.name}`, dataUrl: await fileToDataUrl(f), name: f.name }))
      );
      setNewImages((prev) => [...prev, ...loaded].slice(0, 5));
    } catch {
      setProductFormError("Impossible de charger l'image.");
    }
  };

  const removeNewImage = (id) => setNewImages((prev) => prev.filter((img) => img.id !== id));

  const loadProducts = async () => {
    try {
      setProductsLoading(true);
      const res = await productService.getProducts();
      const list = Array.isArray(res?.data) ? res.data : (res?.data?.products || []);
      setProducts(list);
    } catch (err) {
      console.warn('Erreur chargement produits:', err);
    } finally {
      setProductsLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    const checkWhatsAppStatus = async () => {
      if (createdAgent?.id) {
        try {
          const res = await agentService.getAgentById(createdAgent.id);
          const connections = res?.data?.connections;
          const parsed = typeof connections === 'string' ? JSON.parse(connections) : connections;
          if (parsed?.whatsapp?.status === 'connected') {
            setWhatsappConnected(true);
            setWhatsappPhoneNumber(parsed?.whatsapp?.phoneNumber || null);
          }
        } catch (e) {}
      }
    };
    checkWhatsAppStatus();
  }, [createdAgent?.id]);

  const handleCreateProduct = async () => {
    setProductFormError('');
    if (!newProduct.name.trim()) return setProductFormError('Le nom est requis.');
    if (!newProduct.category.trim()) return setProductFormError('La catégorie est requise.');
    const price = parseFloat(newProduct.price);
    if (newProduct.price === '' || Number.isNaN(price)) return setProductFormError('Le prix est invalide.');

    try {
      setProductSaving(true);
      await productService.createProduct({
        name: newProduct.name.trim(),
        category: newProduct.category.trim(),
        price,
        currency: 'EUR',
        stock: newProduct.stock !== '' && !Number.isNaN(parseInt(newProduct.stock, 10))
          ? parseInt(newProduct.stock, 10)
          : undefined,
        shortDescription: newProduct.shortDescription.trim(),
        description: newProduct.shortDescription.trim() || newProduct.name.trim(),
        images: newImages.map((img) => img.dataUrl),
      });
      setNewProduct({ name: '', category: '', price: '', stock: '', shortDescription: '' });
      setNewImages([]);
      setShowProductForm(false);
      await loadProducts();
    } catch (err) {
      setProductFormError(err?.message || "Erreur lors de l'ajout du produit.");
    } finally {
      setProductSaving(false);
    }
  };

  const handleDeleteProduct = async (id) => {
    try {
      await productService.deleteProduct(id);
      await loadProducts();
    } catch (err) {
      console.error('Erreur suppression produit:', err);
    }
  };

  const formatWhatsappNumber = (raw) => {
    if (!raw) return null;
    const digits = String(raw).replace(/\D/g, '');
    return digits ? `+${digits}` : null;
  };

  const handleStartWhatsAppConnection = async () => {
    try {
      setSaveError('');
      let agentId = createdAgent?.id;
      if (!agentId) {
        const agent = await handleSave();
        if (!agent?.id) {
          setSaveError("Impossible de créer l'agent");
          return;
        }
        agentId = agent.id;
      }

      const response = await agentService.connectWhatsApp(agentId);
      const updatedAgent = response?.data;
      if (!updatedAgent) {
        setSaveError("Pas de données d'agent retournées");
        return;
      }

      let connections = updatedAgent.connections;
      if (typeof connections === 'string') {
        try { connections = JSON.parse(connections); } catch (e) { connections = {}; }
      }

      setCreatedAgent({ ...updatedAgent, connections });

      const sessionId = connections?.whatsapp?.sessionId;
      if (sessionId) {
        setWhatsappSessionId(sessionId);
        setActiveTab('connections');
        setShowQRCode(true);
      } else {
        setSaveError('Pas de session ID reçu du serveur. Vérifiez les logs du backend.');
      }
    } catch (error) {
      let message = 'Erreur lors de la connexion WhatsApp';
      if (error?.response?.status === 404) {
        message = 'Endpoint WhatsApp non trouvé. Vérifiez que le backend implémente bien /agents/:id/whatsapp/connect';
      } else if (error?.response?.status === 500) {
        message = 'Erreur serveur lors de la génération du QR code. Vérifiez les logs du backend.';
      } else if (error?.response?.data?.message) {
        message = error.response.data.message;
      } else if (error?.message) {
        message = error.message;
      }
      setSaveError(message);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    setSaveSuccess('');
    try {
      const agentData = {
        name: agentName,
        type: 'customer_service',
        model: formData.model,
        prompt: formData.prompt,
        temperature: formData.temperature,
        maxTokens: formData.maxTokens,
        language: formData.language,
        timezone: formData.timezone,
        status: 'inactive',
        tools: formData.tools,
        config: {
          style: formData.style,
          responseTime: formData.responseTime,
          workingHours: formData.workingHours,
          autoResponse: formData.autoResponse,
          learningMode: formData.learningMode,
          connections: formData.connections,
          paymentNumbers,
        },
      };
      const existingId = createdAgent?.id;
      const response = existingId
        ? await agentService.updateAgent(existingId, agentData)
        : await agentService.createAgent(agentData);
      const agent = response?.data;
      setCreatedAgent(agent);
      setSaveSuccess('Agent sauvegardé avec succès !');
      return agent;
    } catch (error) {
      let errorMessage = "Erreur lors de la sauvegarde de l'agent";
      if (error.response) {
        if (error.response.status === 404) errorMessage = 'Endpoint non trouvé.';
        else if (error.response.status === 401) errorMessage = 'Non autorisé. Veuillez vous reconnecter.';
        else if (error.response.status === 500) errorMessage = 'Erreur serveur. Vérifiez les logs du backend.';
        else errorMessage = error.response.data?.message || `Erreur ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Impossible de contacter le serveur. Vérifiez que le backend est démarré.';
      } else {
        errorMessage = error.message || 'Erreur inconnue';
      }
      setSaveError(errorMessage);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleDeploy = async () => {
    setDeploying(true);
    setSaveError('');
    setSaveSuccess('');
    try {
      const agentData = {
        name: agentName,
        type: 'customer_service',
        model: formData.model,
        prompt: formData.prompt,
        temperature: formData.temperature,
        maxTokens: formData.maxTokens,
        language: formData.language,
        timezone: formData.timezone,
        status: 'active',
        tools: formData.tools,
        config: {
          style: formData.style,
          responseTime: formData.responseTime,
          workingHours: formData.workingHours,
          autoResponse: formData.autoResponse,
          learningMode: formData.learningMode,
          connections: formData.connections,
          paymentNumbers,
        },
      };
      const existingId = createdAgent?.id;
      const response = existingId
        ? await agentService.updateAgent(existingId, agentData)
        : await agentService.createAgent(agentData);
      const agent = response?.data;
      setCreatedAgent(agent);

      const sid = agent?.connections?.whatsapp?.sessionId;
      if (sid) {
        setWhatsappSessionId(sid);
        setActiveTab('connections');
        setShowQRCode(true);
      }

      if (agent?.id) {
        await agentService.deployAgent(agent.id);
        setSaveSuccess('Agent déployé avec succès ! Redirection vers vos agents…');
        setTimeout(() => navigate('/agents'), 1500);
      }
    } catch (error) {
      let errorMessage = "Erreur lors du déploiement de l'agent";
      if (error.response) {
        if (error.response.status === 404) errorMessage = 'Endpoint non trouvé.';
        else if (error.response.status === 401) errorMessage = 'Non autorisé. Veuillez vous reconnecter.';
        else if (error.response.status === 500) errorMessage = 'Erreur serveur. Vérifiez les logs du backend.';
        else errorMessage = error.response.data?.message || `Erreur ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Impossible de contacter le serveur. Vérifiez que le backend est démarré.';
      } else {
        errorMessage = error.message || 'Erreur inconnue';
      }
      setSaveError(errorMessage);
    } finally {
      setDeploying(false);
    }
  };

  const handlePlaygroundSend = async () => {
    const content = pgInput.trim();
    if (!content || pgLoading) return;
    setPgError('');
    setPgLoading(true);

    let agentId = createdAgent?.id;
    if (!agentId) {
      const agent = await handleSave();
      if (!agent?.id) {
        setPgLoading(false);
        setPgError("Enregistrez l'agent avant de le tester (échec de la création).");
        return;
      }
      agentId = agent.id;
    }

    const history = pgMessages.map((m) => ({ role: m.role, content: m.content }));
    setPgMessages((prev) => [...prev, { role: 'user', content }]);
    setPgInput('');

    try {
      const res = await agentService.testAgent(agentId, {
        message: content,
        history,
        overrides: {
          prompt: formData.prompt,
          model: formData.model,
          temperature: formData.temperature,
          maxTokens: formData.maxTokens,
          style: formData.style,
        },
      });
      const data = res?.data || res || {};
      setPgMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: data.reply || '— (réponse vide)',
          sources: {
            knowledge: data.retrievedKnowledge || [],
            products: data.usedProducts || [],
          },
        },
      ]);
    } catch (err) {
      const raw =
        err?.error?.message ||
        err?.message ||
        (typeof err?.error === 'string' ? err.error : '') ||
        "Erreur lors du test de l'agent.";
      const message = /crédits?\s+épuis/i.test(raw)
        ? 'Crédits épuisés : rechargez votre organisation pour tester un agent.'
        : raw;
      setPgError(message);
    } finally {
      setPgLoading(false);
    }
  };

  const handlePlaygroundKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handlePlaygroundSend();
    }
  };

  /* ─── Spinner keyframe injected once ─── */
  const spinStyle = { animation: 'ca-spin 0.8s linear infinite' };
  const spinStyleBlack = { borderColor: 'rgba(0,0,0,0.2)', borderTopColor: '#000', ...spinStyle };
  const spinStyleGreen = {
    borderColor: 'rgb(var(--c-accent-green-rgb) / 0.3)',
    borderTopColor: 'rgb(var(--c-accent-green-rgb))',
    ...spinStyle,
  };

  return (
    <div className="min-h-screen" style={{ background: 'rgb(var(--c-primary-dark-rgb))' }}>
      <style>{`@keyframes ca-spin { to { transform: rotate(360deg); } }`}</style>

      {/* ── Header ── */}
      <div
        className="border-b sticky top-0 z-50"
        style={{ background: 'rgb(var(--c-primary-card-rgb))', borderColor: 'rgb(var(--c-border-rgb))' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">

            {/* Left: back + name */}
            <div className="flex items-center gap-4 min-w-0">
              <button
                onClick={() => navigate('/agents')}
                className="p-2 rounded-lg transition-colors"
                style={{ color: 'rgb(var(--c-text-muted-rgb))' }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'rgb(var(--c-primary-dark-rgb))')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <ArrowLeft size={20} />
              </button>

              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgb(var(--c-accent-green-rgb))' }}
                >
                  <Bot size={20} style={{ color: '#000' }} />
                </div>

                {isEditingName ? (
                  <motion.div
                    className="flex items-center gap-2"
                    initial={{ opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.15 }}
                  >
                    <input
                      type="text"
                      value={agentName}
                      onChange={(e) => setAgentName(e.target.value)}
                      onBlur={() => setIsEditingName(false)}
                      onKeyPress={(e) => e.key === 'Enter' && setIsEditingName(false)}
                      autoFocus
                      className="text-xl font-bold rounded-lg px-3 py-1.5 outline-none transition-colors"
                      style={{
                        color: 'rgb(var(--c-text-primary-rgb))',
                        background: 'rgb(var(--c-primary-dark-rgb))',
                        border: '2px solid rgb(var(--c-accent-green-rgb))',
                      }}
                    />
                    <button
                      onClick={() => setIsEditingName(false)}
                      className="p-2 rounded-lg"
                      style={{ background: 'rgb(var(--c-accent-green-rgb))', color: '#000' }}
                    >
                      <Check size={15} />
                    </button>
                  </motion.div>
                ) : (
                  <div className="flex items-center gap-2">
                    <div className="cursor-pointer min-w-0" onClick={() => setIsEditingName(true)}>
                      <h1
                        className="text-xl font-bold truncate"
                        style={{ color: 'rgb(var(--c-text-primary-rgb))' }}
                      >
                        {agentName}
                      </h1>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="status-dot" />
                        <p className="text-xs" style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>
                          En ligne · Prêt à répondre
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setIsEditingName(true)}
                      className="p-1.5 rounded-lg transition-colors"
                      style={{ color: 'rgb(var(--c-text-muted-rgb))' }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = 'rgb(var(--c-accent-green-rgb))')}
                      onMouseLeave={(e) => (e.currentTarget.style.color = 'rgb(var(--c-text-muted-rgb))')}
                    >
                      <Edit2 size={14} />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Right: badge + actions */}
            <div className="flex items-center gap-3 flex-wrap">
              <div
                className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-lg"
                style={{ background: 'rgb(var(--c-primary-dark-rgb))', border: '1px solid rgb(var(--c-border-rgb))' }}
              >
                <Cpu size={14} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
                <span className="text-sm" style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>Assistant IA</span>
                <span className="status-dot" />
              </div>

              <button
                onClick={handleSave}
                disabled={saving}
                className="btn-ghost flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 rounded-full border-2" style={spinStyleGreen} />
                    <span>Sauvegarde…</span>
                  </>
                ) : (
                  <>
                    <Save size={15} />
                    <span>Enregistrer</span>
                  </>
                )}
              </button>

              <button
                onClick={handleDeploy}
                disabled={deploying}
                className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deploying ? (
                  <>
                    <div className="w-4 h-4 rounded-full border-2" style={spinStyleBlack} />
                    <span>Déploiement…</span>
                  </>
                ) : (
                  <>
                    <Rocket size={15} />
                    <span>Déployer</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Toast area */}
        <AnimatePresence mode="wait">
          {saveSuccess && (
            <motion.div
              key="success"
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              className="mb-6 flex items-center gap-3 rounded-lg p-4"
              style={{
                background: 'rgb(var(--c-success-rgb) / 0.08)',
                border: '1px solid rgb(var(--c-success-rgb) / 0.3)',
              }}
            >
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgb(var(--c-success-rgb))' }}
              >
                <Check size={15} style={{ color: '#fff' }} />
              </div>
              <p className="text-sm font-medium" style={{ color: 'rgb(var(--c-text-primary-rgb))' }}>
                {saveSuccess}
              </p>
            </motion.div>
          )}
          {saveError && (
            <motion.div
              key="error"
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              className="mb-6 flex items-center gap-3 rounded-lg p-4"
              style={{
                background: 'rgb(var(--c-danger-rgb) / 0.08)',
                border: '1px solid rgb(var(--c-danger-rgb) / 0.3)',
              }}
            >
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgb(var(--c-danger-rgb))' }}
              >
                <X size={15} style={{ color: '#fff' }} />
              </div>
              <div>
                <p className="text-sm font-medium" style={{ color: 'rgb(var(--c-text-primary-rgb))' }}>Erreur</p>
                <p className="text-sm" style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>{saveError}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex flex-col xl:flex-row gap-6">

          {/* ── Tab nav ── */}
          <div className="w-full xl:w-56 flex-shrink-0">
            <div
              className="rounded-xl p-1.5 flex xl:flex-col gap-1 overflow-x-auto"
              style={{ background: 'rgb(var(--c-primary-card-rgb))', border: '1px solid rgb(var(--c-border-rgb))' }}
            >
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const active = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className="flex-shrink-0 xl:w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors"
                    style={{
                      background: active ? 'rgb(var(--c-accent-green-rgb))' : 'transparent',
                      color: active ? '#000' : 'rgb(var(--c-text-muted-rgb))',
                    }}
                    onMouseEnter={(e) => {
                      if (!active) {
                        e.currentTarget.style.background = 'rgb(var(--c-primary-dark-rgb))';
                        e.currentTarget.style.color = 'rgb(var(--c-text-primary-rgb))';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!active) {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = 'rgb(var(--c-text-muted-rgb))';
                      }
                    }}
                  >
                    <Icon size={15} />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Main panel ── */}
          <div className="flex-1 min-w-0">

            {/* PROMPT */}
            {activeTab === 'prompt' && (
              <div className="card p-6 space-y-6">
                <div className="flex items-center gap-2">
                  <Sparkles size={16} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
                  <h3 className="font-semibold" style={{ color: 'rgb(var(--c-text-primary-rgb))' }}>
                    Configuration du prompt
                  </h3>
                  <div
                    className="ml-auto flex items-center gap-1.5 px-3 py-1 rounded-lg"
                    style={{ background: 'rgb(var(--c-accent-green-rgb) / 0.1)' }}
                  >
                    <BarChart3 size={13} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
                    <span className="text-xs font-medium" style={{ color: 'rgb(var(--c-accent-green-rgb))' }}>
                      Score <span className="font-metric">{promptScore}</span>
                      <span style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>/10</span>
                    </span>
                  </div>
                </div>

                {/* Model cards */}
                <div>
                  <label className="block text-sm font-medium mb-3" style={{ color: 'rgb(var(--c-text-primary-rgb))' }}>
                    Modèle d'IA
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {models.map((model, i) => {
                      const Icon = model.icon;
                      const sel = formData.model === model.name;
                      return (
                        <motion.button
                          key={model.id}
                          onClick={() => setFormData({ ...formData, model: model.name })}
                          className="p-4 rounded-xl border text-left transition-colors"
                          initial={{ opacity: 0, y: 14 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.07, duration: 0.22 }}
                          style={{
                            borderColor: sel ? 'rgb(var(--c-accent-green-rgb))' : 'rgb(var(--c-border-rgb))',
                            background: sel ? 'rgb(var(--c-accent-green-rgb) / 0.06)' : 'rgb(var(--c-primary-dark-rgb))',
                          }}
                        >
                          <div className="flex items-center gap-2.5 mb-2.5">
                            <div
                              className="w-8 h-8 rounded-lg flex items-center justify-center"
                              style={{ background: 'rgb(var(--c-accent-green-rgb) / 0.12)' }}
                            >
                              <Icon size={15} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
                            </div>
                            <span className="text-sm font-semibold" style={{ color: 'rgb(var(--c-text-primary-rgb))' }}>
                              {model.name}
                            </span>
                            {sel && (
                              <div
                                className="ml-auto w-4 h-4 rounded-full flex items-center justify-center"
                                style={{ background: 'rgb(var(--c-accent-green-rgb))' }}
                              >
                                <Check size={10} style={{ color: '#000' }} />
                              </div>
                            )}
                          </div>
                          <p className="text-xs mb-3" style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>
                            {model.desc}
                          </p>
                          <div className="space-y-1.5">
                            <div className="flex justify-between text-xs">
                              <span style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>Performance</span>
                              <span className="font-metric" style={{ color: 'rgb(var(--c-accent-green-rgb))' }}>
                                {model.performance}%
                              </span>
                            </div>
                            <div
                              className="w-full rounded-full overflow-hidden"
                              style={{ height: '3px', background: 'rgb(var(--c-border-rgb))' }}
                            >
                              <motion.div
                                className="h-full rounded-full"
                                style={{ background: 'rgb(var(--c-accent-green-rgb))' }}
                                initial={{ width: 0 }}
                                animate={{ width: `${model.performance}%` }}
                                transition={{ delay: 0.35 + i * 0.07, duration: 0.55 }}
                              />
                            </div>
                            <div className="flex justify-between text-xs">
                              <span style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>Vitesse</span>
                              <span className="font-metric" style={{ color: 'rgb(var(--c-accent-green-rgb))' }}>
                                {model.speed}
                              </span>
                            </div>
                          </div>
                        </motion.button>
                      );
                    })}
                  </div>
                </div>

                {/* Communication style */}
                <div>
                  <label className="block text-sm font-medium mb-3" style={{ color: 'rgb(var(--c-text-primary-rgb))' }}>
                    Style de communication
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {communicationStyles.map((style) => {
                      const sel = formData.style === style.name;
                      return (
                        <button
                          key={style.id}
                          onClick={() => setFormData({ ...formData, style: style.name })}
                          className="p-3 rounded-lg border text-left transition-colors"
                          style={{
                            borderColor: sel ? 'rgb(var(--c-accent-green-rgb))' : 'rgb(var(--c-border-rgb))',
                            background: sel ? 'rgb(var(--c-accent-green-rgb) / 0.05)' : 'transparent',
                          }}
                        >
                          <span className="text-sm font-medium block mb-0.5" style={{ color: 'rgb(var(--c-text-primary-rgb))' }}>
                            {style.name}
                          </span>
                          <p className="text-xs" style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>{style.desc}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Char counter */}
                <div>
                  <div className="flex justify-between mb-2">
                    <label className="text-sm font-medium" style={{ color: 'rgb(var(--c-text-primary-rgb))' }}>
                      Caractères utilisés
                    </label>
                    <span className="font-metric text-sm" style={{ color: 'rgb(var(--c-accent-green-rgb))' }}>
                      {formData.prompt.length} / 12 000
                    </span>
                  </div>
                  <div className="w-full rounded-full overflow-hidden" style={{ height: '3px', background: 'rgb(var(--c-border-rgb))' }}>
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${Math.min((formData.prompt.length / 12000) * 100, 100)}%`,
                        background: 'rgb(var(--c-accent-green-rgb))',
                      }}
                    />
                  </div>
                </div>

                {/* Prompt textarea */}
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'rgb(var(--c-text-primary-rgb))' }}>
                    Personnalisation de l'agent
                  </label>
                  <p className="text-xs mb-3" style={{ color: 'rgb(var(--c-text-secondary-rgb))' }}>
                    Décris la personnalité, le nom et les spécificités de ta boutique. La gestion des paiements, la proactivité commerciale et les comportements de base sont assurés automatiquement par le système.
                  </p>
                  <textarea
                    value={formData.prompt}
                    onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                    rows={16}
                    placeholder="Ex : Je suis l'assistant de [Boutique], spécialisé(e) dans [niche]. Mon ton est [chaleureux / professionnel]…"
                    className="w-full rounded-lg p-4 font-mono text-sm resize-none outline-none transition-colors"
                    style={{
                      background: 'rgb(var(--c-primary-dark-rgb))',
                      color: 'rgb(var(--c-text-primary-rgb))',
                      border: '1px solid rgb(var(--c-border-rgb))',
                    }}
                    onFocus={(e) => (e.target.style.borderColor = 'rgb(var(--c-accent-green-rgb))')}
                    onBlur={(e) => (e.target.style.borderColor = 'rgb(var(--c-border-rgb))')}
                  />
                </div>
              </div>
            )}

            {/* KNOWLEDGE */}
            {activeTab === 'knowledge' && (
              <div className="card p-6">
                <div className="flex items-center gap-2 mb-6">
                  <Database size={16} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
                  <h3 className="font-semibold" style={{ color: 'rgb(var(--c-text-primary-rgb))' }}>
                    Base de connaissances
                  </h3>
                </div>

                <div className="text-center py-12">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                    style={{ background: 'rgb(var(--c-accent-green-rgb) / 0.1)' }}
                  >
                    <FileText size={30} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
                  </div>
                  <h4 className="font-semibold mb-2" style={{ color: 'rgb(var(--c-text-primary-rgb))' }}>
                    Aucun document ajouté
                  </h4>
                  <p className="text-sm mb-6" style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>
                    Ajoutez des documents pour enrichir les connaissances de votre agent
                  </p>
                  <button className="btn-primary">+ Ajouter des documents</button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
                  {[
                    { Icon: FileText, label: 'PDF', sub: 'Guides, manuels' },
                    { Icon: Globe, label: 'Sites web', sub: 'URLs, pages' },
                    { Icon: Database, label: 'Données', sub: 'CSV, JSON' },
                  ].map(({ Icon, label, sub }) => (
                    <div
                      key={label}
                      className="p-4 rounded-lg text-center"
                      style={{ background: 'rgb(var(--c-primary-dark-rgb))' }}
                    >
                      <Icon size={20} className="mx-auto mb-2" style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
                      <p className="text-sm font-semibold" style={{ color: 'rgb(var(--c-text-primary-rgb))' }}>{label}</p>
                      <p className="text-xs" style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>{sub}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* PRODUCTS */}
            {activeTab === 'products' && (
              <div className="card p-4 sm:p-6">
                <div className="flex items-center gap-2 mb-1">
                  <Package size={16} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
                  <h3 className="font-semibold" style={{ color: 'rgb(var(--c-text-primary-rgb))' }}>
                    Produits et services
                  </h3>
                </div>
                <p className="text-sm mb-6" style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>
                  Ces produits appartiennent à votre catalogue et sont utilisés automatiquement par l'agent pour répondre aux clients.
                </p>

                {!showProductForm ? (
                  <div className="mb-6">
                    <button
                      type="button"
                      onClick={() => { setShowProductForm(true); setProductFormError(''); }}
                      className="w-full p-4 rounded-lg border-2 border-dashed flex items-center justify-center gap-2 text-sm font-medium transition-colors"
                      style={{ borderColor: 'rgb(var(--c-border-rgb))', color: 'rgb(var(--c-text-muted-rgb))' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = 'rgb(var(--c-accent-green-rgb))';
                        e.currentTarget.style.color = 'rgb(var(--c-accent-green-rgb))';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'rgb(var(--c-border-rgb))';
                        e.currentTarget.style.color = 'rgb(var(--c-text-muted-rgb))';
                      }}
                    >
                      <Package size={16} />
                      <span>Ajouter un produit ou service</span>
                    </button>
                  </div>
                ) : (
                  <div
                    className="mb-6 rounded-lg p-4 space-y-3"
                    style={{ background: 'rgb(var(--c-primary-dark-rgb))', border: '1px solid rgb(var(--c-border-rgb))' }}
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {[
                        { key: 'name', label: 'Nom *', type: 'text', placeholder: 'Ex: Chatbot Premium' },
                        { key: 'category', label: 'Catégorie *', type: 'text', placeholder: 'Ex: Service' },
                        { key: 'price', label: 'Prix (€) *', type: 'number', placeholder: '99', extra: { step: '0.01' } },
                        { key: 'stock', label: 'Stock', type: 'number', placeholder: 'Illimité si vide', extra: { min: '0' } },
                      ].map(({ key, label, type, placeholder, extra }) => (
                        <div key={key}>
                          <label className="text-xs mb-1 block" style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>{label}</label>
                          <input
                            type={type}
                            value={newProduct[key]}
                            onChange={(e) => setNewProduct({ ...newProduct, [key]: e.target.value })}
                            placeholder={placeholder}
                            {...(extra || {})}
                            className="w-full rounded-lg px-3 py-2 text-sm outline-none transition-colors"
                            style={{
                              background: 'rgb(var(--c-primary-card-rgb))',
                              color: 'rgb(var(--c-text-primary-rgb))',
                              border: '1px solid rgb(var(--c-border-rgb))',
                            }}
                            onFocus={(e) => (e.target.style.borderColor = 'rgb(var(--c-accent-green-rgb))')}
                            onBlur={(e) => (e.target.style.borderColor = 'rgb(var(--c-border-rgb))')}
                          />
                        </div>
                      ))}
                      <div className="sm:col-span-2">
                        <label className="text-xs mb-1 block" style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>Description courte</label>
                        <input
                          type="text"
                          value={newProduct.shortDescription}
                          onChange={(e) => setNewProduct({ ...newProduct, shortDescription: e.target.value })}
                          placeholder="Optionnel"
                          className="w-full rounded-lg px-3 py-2 text-sm outline-none transition-colors"
                          style={{
                            background: 'rgb(var(--c-primary-card-rgb))',
                            color: 'rgb(var(--c-text-primary-rgb))',
                            border: '1px solid rgb(var(--c-border-rgb))',
                          }}
                          onFocus={(e) => (e.target.style.borderColor = 'rgb(var(--c-accent-green-rgb))')}
                          onBlur={(e) => (e.target.style.borderColor = 'rgb(var(--c-border-rgb))')}
                        />
                      </div>
                    </div>

                    {/* Images */}
                    <div>
                      <label className="text-xs mb-1 block" style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>
                        Images (max 5, 2 Mo chacune)
                      </label>
                      <div className="flex items-center gap-2 flex-wrap">
                        {newImages.map((img) => (
                          <div
                            key={img.id}
                            className="relative w-14 h-14 rounded-lg overflow-hidden"
                            style={{ border: '1px solid rgb(var(--c-border-rgb))' }}
                          >
                            <img src={img.dataUrl} alt={img.name} className="w-full h-full object-cover" />
                            <button
                              type="button"
                              onClick={() => removeNewImage(img.id)}
                              className="absolute top-0.5 right-0.5 p-0.5 rounded"
                              style={{ background: 'rgb(var(--c-danger-rgb))', color: '#fff' }}
                            >
                              <X size={10} />
                            </button>
                          </div>
                        ))}
                        {newImages.length < 5 && (
                          <label
                            className="w-14 h-14 rounded-lg border-2 border-dashed flex items-center justify-center cursor-pointer transition-colors"
                            style={{ borderColor: 'rgb(var(--c-border-rgb))', color: 'rgb(var(--c-text-muted-rgb))' }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = 'rgb(var(--c-accent-green-rgb))';
                              e.currentTarget.style.color = 'rgb(var(--c-accent-green-rgb))';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = 'rgb(var(--c-border-rgb))';
                              e.currentTarget.style.color = 'rgb(var(--c-text-muted-rgb))';
                            }}
                          >
                            <Upload size={15} />
                            <input type="file" accept="image/*" multiple onChange={handleProductImageSelect} className="hidden" />
                          </label>
                        )}
                      </div>
                    </div>

                    {productFormError && (
                      <p className="text-xs" style={{ color: 'rgb(var(--c-danger-rgb))' }}>{productFormError}</p>
                    )}

                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => { setShowProductForm(false); setProductFormError(''); setNewImages([]); }}
                        className="flex-1 py-2 rounded-lg text-sm font-medium transition-colors"
                        style={{ background: 'rgb(var(--c-primary-card-rgb))', color: 'rgb(var(--c-text-muted-rgb))' }}
                      >
                        Annuler
                      </button>
                      <button
                        type="button"
                        onClick={handleCreateProduct}
                        disabled={productSaving}
                        className="flex-1 py-2 rounded-lg text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                        style={{ background: 'rgb(var(--c-accent-green-rgb))', color: '#000' }}
                      >
                        {productSaving
                          ? <div className="w-4 h-4 rounded-full border-2" style={spinStyleBlack} />
                          : <Check size={14} />
                        }
                        Ajouter
                      </button>
                    </div>
                  </div>
                )}

                {productsLoading ? (
                  <div className="flex items-center justify-center py-10 gap-2" style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>
                    <div className="w-5 h-5 rounded-full border-2" style={spinStyleGreen} />
                    Chargement des produits…
                  </div>
                ) : products.length === 0 ? (
                  <div className="text-center py-10">
                    <Package size={36} className="mx-auto mb-3" style={{ color: 'rgb(var(--c-border-rgb))' }} />
                    <p className="text-sm" style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>
                      Aucun produit dans votre catalogue pour l'instant.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {products.map((product) => (
                      <div
                        key={product._id}
                        className="flex items-center justify-between gap-3 p-4 rounded-lg"
                        style={{ background: 'rgb(var(--c-primary-dark-rgb))' }}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div
                            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden"
                            style={{ background: 'rgb(var(--c-accent-green-rgb) / 0.1)' }}
                          >
                            {product.images?.[0] ? (
                              <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                            ) : (
                              <Package size={16} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
                            )}
                          </div>
                          <div className="min-w-0">
                            <h4 className="text-sm font-semibold truncate" style={{ color: 'rgb(var(--c-text-primary-rgb))' }}>
                              {product.name}
                            </h4>
                            <p className="text-xs truncate" style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>
                              {product.category}{product.category ? ' · ' : ''}
                              <span className="font-metric">{product.price}€</span>
                              {typeof product.stock === 'number' ? ` · ${product.stock} en stock` : ''}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDeleteProduct(product._id)}
                          className="text-xs flex-shrink-0 transition-colors"
                          style={{ color: 'rgb(var(--c-text-muted-rgb))' }}
                          onMouseEnter={(e) => (e.currentTarget.style.color = 'rgb(var(--c-danger-rgb))')}
                          onMouseLeave={(e) => (e.currentTarget.style.color = 'rgb(var(--c-text-muted-rgb))')}
                        >
                          Supprimer
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* CONNECTIONS */}
            {activeTab === 'connections' && (
              <div className="card p-6">
                <div className="flex items-center gap-2 mb-6">
                  <MessageSquare size={16} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
                  <h3 className="font-semibold" style={{ color: 'rgb(var(--c-text-primary-rgb))' }}>
                    Connecter WhatsApp
                  </h3>
                </div>

                {!whatsappConnected ? (
                  <div>
                    {/* Method switcher */}
                    <div
                      className="flex gap-2 mb-6 p-1 rounded-lg"
                      style={{ background: 'rgb(var(--c-primary-dark-rgb))' }}
                    >
                      {[
                        { id: 'qr', label: 'QR Code', Icon: QrCode },
                        { id: 'phone', label: 'Numéro de téléphone', Icon: Smartphone },
                      ].map(({ id, label, Icon }) => (
                        <button
                          key={id}
                          type="button"
                          onClick={() => setConnMethod(id)}
                          className="flex-1 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-1.5 transition-colors"
                          style={{
                            background: connMethod === id ? 'rgb(var(--c-accent-green-rgb))' : 'transparent',
                            color: connMethod === id ? '#000' : 'rgb(var(--c-text-muted-rgb))',
                          }}
                        >
                          <Icon size={14} />
                          {label}
                        </button>
                      ))}
                    </div>

                    {connMethod === 'qr' && (
                      <div className="rounded-lg p-6 mb-6" style={{ background: 'rgb(var(--c-primary-dark-rgb))' }}>
                        <div className="flex items-start gap-4 mb-6">
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ background: 'rgb(var(--c-success-rgb) / 0.15)' }}
                          >
                            <MessageSquare size={18} style={{ color: 'rgb(var(--c-success-rgb))' }} />
                          </div>
                          <div>
                            <h4 className="font-semibold mb-2" style={{ color: 'rgb(var(--c-text-primary-rgb))' }}>
                              Connectez votre compte WhatsApp
                            </h4>
                            <p className="text-sm mb-4" style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>
                              Pour permettre à votre agent de communiquer via WhatsApp, vous devez connecter votre compte WhatsApp Business.
                            </p>
                            <ul className="space-y-2 text-sm" style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>
                              {[
                                'Répondez automatiquement aux messages WhatsApp',
                                'Gérez toutes vos conversations depuis un seul endroit',
                                'Synchronisation en temps réel',
                              ].map((item) => (
                                <li key={item} className="flex items-start gap-2">
                                  <Check size={13} className="mt-0.5 flex-shrink-0" style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>

                        {!showQRCode ? (
                          <button
                            onClick={handleStartWhatsAppConnection}
                            className="btn-primary w-full flex items-center justify-center gap-2"
                          >
                            <QrCode size={16} />
                            Lancer la connexion
                          </button>
                        ) : (
                          <div>
                            <WhatsAppQRConnect
                              sessionId={whatsappSessionId}
                              onConnected={(phone) => {
                                setWhatsappPhoneNumber(phone || null);
                                setWhatsappConnected(true);
                                setShowQRCode(false);
                                setWhatsappSessionId(null);
                                setSaveSuccess('WhatsApp connecté avec succès !');
                              }}
                            />
                            <button
                              onClick={() => { setShowQRCode(false); setWhatsappSessionId(null); }}
                              className="w-full mt-4 py-2.5 rounded-lg text-sm transition-colors"
                              style={{ background: 'rgb(var(--c-primary-card-rgb))', color: 'rgb(var(--c-text-muted-rgb))' }}
                            >
                              Annuler
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {connMethod === 'phone' && (
                      <WhatsAppPhoneConnect
                        sessionLabel={agentName}
                        onConnected={(phone) => {
                          setWhatsappPhoneNumber(phone || null);
                          setWhatsappConnected(true);
                        }}
                      />
                    )}
                  </div>
                ) : (
                  <div>
                    <div
                      className="rounded-lg p-5 mb-6"
                      style={{
                        background: 'rgb(var(--c-success-rgb) / 0.07)',
                        border: '1px solid rgb(var(--c-success-rgb) / 0.3)',
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ background: 'rgb(var(--c-success-rgb))' }}
                        >
                          <Check size={17} style={{ color: '#fff' }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold mb-1" style={{ color: 'rgb(var(--c-text-primary-rgb))' }}>
                            WhatsApp connecté avec succès !
                          </h4>
                          <p className="text-sm mb-3" style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>
                            Votre agent peut maintenant recevoir et répondre aux messages WhatsApp automatiquement.
                          </p>
                          <div className="flex flex-wrap items-center gap-4 text-sm">
                            <div className="flex items-center gap-1.5">
                              <span className="status-dot" />
                              <span style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>Connecté</span>
                            </div>
                            <div className="flex items-center gap-1.5 min-w-0">
                              <span style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>Compte :</span>
                              <span className="font-medium truncate" style={{ color: 'rgb(var(--c-text-primary-rgb))' }}>
                                {formatWhatsappNumber(whatsappPhoneNumber) || 'Numéro non disponible'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-lg p-5" style={{ background: 'rgb(var(--c-primary-dark-rgb))' }}>
                      <h5 className="font-semibold mb-4" style={{ color: 'rgb(var(--c-text-primary-rgb))' }}>
                        Paramètres WhatsApp
                      </h5>
                      <div className="space-y-4">
                        {[
                          { label: 'Réponses automatiques', sub: "L'agent répond automatiquement aux messages" },
                          { label: 'Notifications', sub: 'Recevoir une notification pour chaque message' },
                        ].map(({ label, sub }) => (
                          <div key={label} className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium" style={{ color: 'rgb(var(--c-text-primary-rgb))' }}>{label}</p>
                              <p className="text-xs" style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>{sub}</p>
                            </div>
                            <div
                              className="w-11 h-6 rounded-full cursor-pointer flex-shrink-0"
                              style={{ background: 'rgb(var(--c-accent-green-rgb))' }}
                            >
                              <div className="w-4 h-4 bg-white rounded-full m-1 translate-x-5" />
                            </div>
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={() => { setWhatsappConnected(false); setWhatsappPhoneNumber(null); }}
                        className="w-full mt-5 py-2.5 rounded-lg text-sm font-medium transition-colors"
                        style={{ background: 'rgb(var(--c-danger-rgb) / 0.12)', color: 'rgb(var(--c-danger-rgb))' }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = 'rgb(var(--c-danger-rgb) / 0.2)')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'rgb(var(--c-danger-rgb) / 0.12)')}
                      >
                        Déconnecter WhatsApp
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* PAYMENT */}
            {activeTab === 'payment' && (
              <div className="card p-6">
                <div className="flex items-center gap-2 mb-1">
                  <CreditCard size={16} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
                  <h3 className="font-semibold" style={{ color: 'rgb(var(--c-text-primary-rgb))' }}>
                    Numéros de paiement
                  </h3>
                  <span
                    className="ml-auto text-xs px-2 py-0.5 rounded-full"
                    style={{ background: 'rgb(var(--c-primary-dark-rgb))', color: 'rgb(var(--c-text-muted-rgb))', border: '1px solid rgb(var(--c-border-rgb))' }}
                  >
                    Optionnel
                  </span>
                </div>
                <p className="text-sm mb-8" style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>
                  Renseignez vos numéros de réception de paiement. L'agent les communiquera automatiquement au client lorsqu'il devra effectuer un règlement.
                </p>

                <div className="space-y-4">

                  {/* Orange Money */}
                  <div
                    className="rounded-xl overflow-hidden"
                    style={{ border: '1px solid rgb(var(--c-border-rgb))' }}
                  >
                    <div
                      className="px-4 py-3 flex items-center gap-3"
                      style={{ background: 'rgba(255,102,0,0.07)', borderBottom: '1px solid rgba(255,102,0,0.2)' }}
                    >
                      <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: 'rgba(255,102,0,0.18)' }}
                      >
                        <Phone size={16} style={{ color: '#FF6600' }} />
                      </div>
                      <div>
                        <p className="text-sm font-bold" style={{ color: '#FF6600' }}>Orange Money</p>
                        <p className="text-xs" style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>
                          Réseau Orange — Côte d'Ivoire, Sénégal, Mali…
                        </p>
                      </div>
                      {paymentNumbers.orangeMoney && (
                        <div
                          className="ml-auto flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
                          style={{ background: 'rgba(255,102,0,0.15)', color: '#FF6600' }}
                        >
                          <Check size={10} />
                          Configuré
                        </div>
                      )}
                    </div>
                    <div className="p-4" style={{ background: 'rgb(var(--c-primary-dark-rgb))' }}>
                      <label className="block text-xs font-medium mb-2" style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>
                        Numéro de téléphone
                      </label>
                      <input
                        type="tel"
                        value={paymentNumbers.orangeMoney}
                        onChange={(e) => setPaymentNumbers((p) => ({ ...p, orangeMoney: e.target.value }))}
                        placeholder="Ex : +225 07 00 00 00 00"
                        className="w-full rounded-lg px-4 py-3 text-sm outline-none transition-colors"
                        style={{
                          background: 'rgb(var(--c-primary-card-rgb))',
                          color: 'rgb(var(--c-text-primary-rgb))',
                          border: '1px solid rgb(var(--c-border-rgb))',
                        }}
                        onFocus={(e) => (e.target.style.borderColor = '#FF6600')}
                        onBlur={(e) => (e.target.style.borderColor = 'rgb(var(--c-border-rgb))')}
                      />
                    </div>
                  </div>

                  {/* MTN Money */}
                  <div
                    className="rounded-xl overflow-hidden"
                    style={{ border: '1px solid rgb(var(--c-border-rgb))' }}
                  >
                    <div
                      className="px-4 py-3 flex items-center gap-3"
                      style={{ background: 'rgba(255,204,0,0.07)', borderBottom: '1px solid rgba(255,204,0,0.25)' }}
                    >
                      <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: 'rgba(255,204,0,0.18)' }}
                      >
                        <Phone size={16} style={{ color: '#D4A800' }} />
                      </div>
                      <div>
                        <p className="text-sm font-bold" style={{ color: '#D4A800' }}>MTN Money</p>
                        <p className="text-xs" style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>
                          Réseau MTN — Côte d'Ivoire, Ghana, Cameroun…
                        </p>
                      </div>
                      {paymentNumbers.mtnMoney && (
                        <div
                          className="ml-auto flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
                          style={{ background: 'rgba(255,204,0,0.15)', color: '#D4A800' }}
                        >
                          <Check size={10} />
                          Configuré
                        </div>
                      )}
                    </div>
                    <div className="p-4" style={{ background: 'rgb(var(--c-primary-dark-rgb))' }}>
                      <label className="block text-xs font-medium mb-2" style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>
                        Numéro de téléphone
                      </label>
                      <input
                        type="tel"
                        value={paymentNumbers.mtnMoney}
                        onChange={(e) => setPaymentNumbers((p) => ({ ...p, mtnMoney: e.target.value }))}
                        placeholder="Ex : +225 05 00 00 00 00"
                        className="w-full rounded-lg px-4 py-3 text-sm outline-none transition-colors"
                        style={{
                          background: 'rgb(var(--c-primary-card-rgb))',
                          color: 'rgb(var(--c-text-primary-rgb))',
                          border: '1px solid rgb(var(--c-border-rgb))',
                        }}
                        onFocus={(e) => (e.target.style.borderColor = '#FFCC00')}
                        onBlur={(e) => (e.target.style.borderColor = 'rgb(var(--c-border-rgb))')}
                      />
                    </div>
                  </div>

                </div>

                {/* Info banner */}
                <div
                  className="mt-6 flex items-start gap-3 rounded-xl p-4"
                  style={{ background: 'rgba(var(--c-accent-green-rgb), 0.06)', border: '1px solid rgba(var(--c-accent-green-rgb), 0.2)' }}
                >
                  <Zap size={15} className="flex-shrink-0 mt-0.5" style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
                  <p className="text-xs leading-relaxed" style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>
                    Ces numéros sont transmis automatiquement par l'agent dans la conversation lorsqu'un client confirme sa commande et doit procéder au paiement. Seuls les numéros renseignés seront mentionnés.
                  </p>
                </div>
              </div>
            )}

            {/* TOOLS */}
            {activeTab === 'tools' && (
              <div className="card p-6">
                <div className="flex items-center gap-2 mb-2">
                  <Wrench size={16} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
                  <h3 className="font-semibold" style={{ color: 'rgb(var(--c-text-primary-rgb))' }}>
                    Outils d'organisation
                  </h3>
                </div>
                <p className="text-sm mb-6" style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>
                  Connectez vos outils d'organisation pour permettre à votre agent de gérer automatiquement vos rendez-vous, tâches et calendrier.
                </p>

                <div className="space-y-4">
                  {[
                    {
                      key: 'googleCalendar',
                      label: 'Google Calendar',
                      sub: 'Gestion de calendrier et événements',
                      Icon: Calendar,
                      iconColor: 'rgb(var(--c-info-rgb))',
                      btnLabel: 'Connecter Google Calendar',
                      connectedLabel: 'user@gmail.com',
                      connectedKey: 'Compte connecté',
                      features: ['Synchronisation automatique des événements', 'Création de rendez-vous via conversation', 'Rappels et notifications automatiques'],
                      onConnect: () => handleToggleGoogleCalendar(true),
                      onDisconnect: () => handleToggleGoogleCalendar(false),
                    },
                    {
                      key: 'notion',
                      label: 'Notion',
                      sub: 'Gestion de tâches et notes',
                      Icon: BookOpen,
                      iconColor: 'rgb(var(--c-text-muted-rgb))',
                      btnLabel: 'Connecter Notion',
                      connectedLabel: 'Mon Workspace',
                      connectedKey: 'Workspace',
                      features: ['Création automatique de tâches', 'Accès aux bases de données Notion', 'Mise à jour des statuts en temps réel'],
                      onConnect: () => setConnectedTools({ ...connectedTools, notion: true }),
                      onDisconnect: () => setConnectedTools({ ...connectedTools, notion: false }),
                    },
                    {
                      key: 'calendly',
                      label: 'Calendly',
                      sub: 'Prise de rendez-vous automatique',
                      Icon: Video,
                      iconColor: 'rgb(var(--c-info-rgb))',
                      btnLabel: 'Connecter Calendly',
                      connectedLabel: 'calendly.com/user',
                      connectedKey: 'Lien Calendly',
                      features: ['Proposition automatique de créneaux disponibles', 'Envoi de liens de réservation personnalisés', 'Confirmation et rappels automatiques'],
                      onConnect: () => setConnectedTools({ ...connectedTools, calendly: true }),
                      onDisconnect: () => setConnectedTools({ ...connectedTools, calendly: false }),
                    },
                  ].map(({ key, label, sub, Icon, iconColor, btnLabel, connectedLabel, connectedKey, features, onConnect, onDisconnect }) => {
                    const connected = connectedTools[key];
                    return (
                      <div
                        key={key}
                        className="p-5 rounded-lg border transition-colors"
                        style={{
                          borderColor: connected ? 'rgb(var(--c-accent-green-rgb))' : 'rgb(var(--c-border-rgb))',
                          background: connected ? 'rgb(var(--c-accent-green-rgb) / 0.04)' : 'rgb(var(--c-primary-dark-rgb))',
                        }}
                      >
                        <div className="flex items-start gap-4">
                          <div
                            className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{ background: `${iconColor.replace(')', ' / 0.12)')}` }}
                          >
                            <Icon size={22} style={{ color: iconColor }} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <h4 className="font-bold text-sm" style={{ color: 'rgb(var(--c-text-primary-rgb))' }}>{label}</h4>
                                <p className="text-xs" style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>{sub}</p>
                              </div>
                              {connected && (
                                <div
                                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                                  style={{ background: 'rgb(var(--c-success-rgb) / 0.15)', color: 'rgb(var(--c-success-rgb))' }}
                                >
                                  <span className="status-dot" /> Connecté
                                </div>
                              )}
                            </div>
                            {!connected ? (
                              <>
                                <ul className="space-y-1.5 mb-4 text-xs" style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>
                                  {features.map((f) => (
                                    <li key={f} className="flex items-start gap-2">
                                      <Check size={11} className="mt-0.5 flex-shrink-0" style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
                                      <span>{f}</span>
                                    </li>
                                  ))}
                                </ul>
                                <button
                                  onClick={onConnect}
                                  className="w-full py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-opacity"
                                  style={{ background: iconColor, color: '#fff' }}
                                  onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.88')}
                                  onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                                >
                                  <Icon size={14} /> {btnLabel}
                                </button>
                              </>
                            ) : (
                              <div className="space-y-2">
                                <div
                                  className="rounded-lg p-3 flex items-center justify-between text-xs"
                                  style={{ background: 'rgb(var(--c-primary-card-rgb))' }}
                                >
                                  <span style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>{connectedKey} :</span>
                                  <span className="font-medium" style={{ color: 'rgb(var(--c-text-primary-rgb))' }}>{connectedLabel}</span>
                                </div>
                                <button
                                  onClick={onDisconnect}
                                  className="w-full py-2 rounded-lg text-xs font-medium transition-colors"
                                  style={{ background: 'rgb(var(--c-danger-rgb) / 0.12)', color: 'rgb(var(--c-danger-rgb))' }}
                                  onMouseEnter={(e) => (e.currentTarget.style.background = 'rgb(var(--c-danger-rgb) / 0.2)')}
                                  onMouseLeave={(e) => (e.currentTarget.style.background = 'rgb(var(--c-danger-rgb) / 0.12)')}
                                >
                                  Déconnecter
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* SETTINGS */}
            {activeTab === 'settings' && (
              <div className="card p-6">
                <div className="flex items-center gap-2 mb-6">
                  <Wrench size={16} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
                  <h3 className="font-semibold" style={{ color: 'rgb(var(--c-text-primary-rgb))' }}>
                    Paramètres avancés
                  </h3>
                </div>

                <div className="space-y-6">
                  {/* Temperature */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="text-sm font-medium flex items-center gap-2" style={{ color: 'rgb(var(--c-text-primary-rgb))' }}>
                        <Zap size={14} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
                        Créativité (Température)
                      </label>
                      <span className="font-metric text-sm" style={{ color: 'rgb(var(--c-accent-green-rgb))' }}>
                        {formData.temperature}
                      </span>
                    </div>
                    <input
                      type="range" min="0" max="1" step="0.1"
                      value={formData.temperature}
                      onChange={(e) => setFormData({ ...formData, temperature: parseFloat(e.target.value) })}
                      className="w-full"
                      style={{ accentColor: 'rgb(var(--c-accent-green-rgb))' }}
                    />
                    <div className="flex justify-between text-xs mt-1" style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>
                      <span>Précis</span>
                      <span>Créatif</span>
                    </div>
                  </div>

                  {/* Language */}
                  <div>
                    <label className="block text-sm font-medium mb-3 flex items-center gap-2" style={{ color: 'rgb(var(--c-text-primary-rgb))' }}>
                      <Globe size={14} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
                      Langue principale
                    </label>
                    <select
                      value={formData.language}
                      onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg outline-none transition-colors"
                      style={{
                        background: 'rgb(var(--c-primary-dark-rgb))',
                        color: 'rgb(var(--c-text-primary-rgb))',
                        border: '1px solid rgb(var(--c-border-rgb))',
                      }}
                      onFocus={(e) => (e.target.style.borderColor = 'rgb(var(--c-accent-green-rgb))')}
                      onBlur={(e) => (e.target.style.borderColor = 'rgb(var(--c-border-rgb))')}
                    >
                      <option>Français</option>
                      <option>English</option>
                      <option>Español</option>
                      <option>Deutsch</option>
                    </select>
                  </div>

                  {/* Working hours */}
                  <div>
                    <label className="block text-sm font-medium mb-3 flex items-center gap-2" style={{ color: 'rgb(var(--c-text-primary-rgb))' }}>
                      <Clock size={14} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
                      Heures de travail
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      {[{ key: 'start', label: 'Début' }, { key: 'end', label: 'Fin' }].map(({ key, label }) => (
                        <div key={key}>
                          <label className="text-xs mb-2 block" style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>{label}</label>
                          <input
                            type="time"
                            value={formData.workingHours[key]}
                            onChange={(e) => setFormData({ ...formData, workingHours: { ...formData.workingHours, [key]: e.target.value } })}
                            className="w-full px-4 py-3 rounded-lg outline-none transition-colors"
                            style={{
                              background: 'rgb(var(--c-primary-dark-rgb))',
                              color: 'rgb(var(--c-text-primary-rgb))',
                              border: '1px solid rgb(var(--c-border-rgb))',
                            }}
                            onFocus={(e) => (e.target.style.borderColor = 'rgb(var(--c-accent-green-rgb))')}
                            onBlur={(e) => (e.target.style.borderColor = 'rgb(var(--c-border-rgb))')}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Toggles */}
                  <div className="space-y-3">
                    {[
                      { Icon: Brain, key: 'learningMode', label: 'Mode apprentissage', sub: "L'agent apprend de chaque conversation" },
                      { Icon: Shield, key: 'autoResponse', label: 'Réponse automatique', sub: 'Répond automatiquement aux messages' },
                    ].map(({ Icon, key, label, sub }) => (
                      <div
                        key={key}
                        className="flex items-center justify-between p-4 rounded-lg"
                        style={{ background: 'rgb(var(--c-primary-dark-rgb))' }}
                      >
                        <div className="flex items-center gap-3">
                          <Icon size={16} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
                          <div>
                            <p className="text-sm font-semibold" style={{ color: 'rgb(var(--c-text-primary-rgb))' }}>{label}</p>
                            <p className="text-xs" style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>{sub}</p>
                          </div>
                        </div>
                        <div
                          onClick={() => setFormData({ ...formData, [key]: !formData[key] })}
                          className="w-11 h-6 rounded-full cursor-pointer transition-colors flex-shrink-0"
                          style={{ background: formData[key] ? 'rgb(var(--c-accent-green-rgb))' : 'rgb(var(--c-border-rgb))' }}
                        >
                          <div
                            className="w-4 h-4 bg-white rounded-full m-1 transition-transform"
                            style={{ transform: formData[key] ? 'translateX(20px)' : 'translateX(0)' }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Playground ── */}
          <div className="w-full xl:w-80 flex-shrink-0">
            <div
              className="rounded-xl xl:sticky xl:top-24 overflow-hidden"
              style={{ background: 'rgb(var(--c-primary-card-rgb))', border: '1px solid rgb(var(--c-border-rgb))' }}
            >
              {/* Header */}
              <div
                className="p-4 flex items-center justify-between"
                style={{ borderBottom: '1px solid rgb(var(--c-border-rgb))' }}
              >
                <div className="flex items-center gap-2">
                  <Eye size={15} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
                  <span className="font-semibold text-sm" style={{ color: 'rgb(var(--c-text-primary-rgb))' }}>
                    Playground
                  </span>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="text-xs" style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>Devise dynamique</span>
                  <div
                    onClick={() => setIsPlayground(!isPlayground)}
                    className="w-9 h-5 rounded-full transition-colors cursor-pointer"
                    style={{ background: isPlayground ? 'rgb(var(--c-accent-green-rgb))' : 'rgb(var(--c-border-rgb))' }}
                  >
                    <div
                      className="w-3.5 h-3.5 bg-white rounded-full m-0.5 transition-transform"
                      style={{ transform: isPlayground ? 'translateX(16px)' : 'translateX(0)' }}
                    />
                  </div>
                </label>
              </div>

              <div className="p-5">
                {pgMessages.length === 0 ? (
                  <>
                    <div className="text-center mb-5">
                      <div
                        className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
                        style={{ background: 'rgb(var(--c-accent-green-rgb) / 0.1)' }}
                      >
                        <Bot size={24} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
                      </div>
                      <h4 className="text-sm font-bold mb-1" style={{ color: 'rgb(var(--c-text-primary-rgb))' }}>
                        Testez {agentName} ici
                      </h4>
                      <p className="text-xs" style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>
                        Envoyez un message pour commencer.
                      </p>
                    </div>
                    <div
                      className="rounded-lg p-3 mb-4 text-center text-xs"
                      style={{ background: 'rgb(var(--c-primary-dark-rgb))', color: 'rgb(var(--c-text-muted-rgb))' }}
                    >
                      Mode test — rien n'est envoyé au client.
                    </div>
                  </>
                ) : (
                  <div className="space-y-3 mb-4 max-h-80 overflow-y-auto pr-1">
                    {pgMessages.map((m, i) => (
                      <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div
                          className="max-w-[85%] px-3 py-2 text-sm whitespace-pre-wrap"
                          style={{
                            background: m.role === 'user' ? 'rgb(var(--c-accent-green-rgb))' : 'rgb(var(--c-primary-dark-rgb))',
                            color: m.role === 'user' ? '#000' : 'rgb(var(--c-text-primary-rgb))',
                            borderRadius: m.role === 'user' ? '12px 12px 3px 12px' : '12px 12px 12px 3px',
                          }}
                        >
                          {m.content}
                          {m.role === 'assistant' && m.sources && (m.sources.knowledge.length > 0 || m.sources.products.length > 0) && (
                            <div className="mt-2 pt-2 space-y-1" style={{ borderTop: '1px solid rgb(var(--c-border-rgb))' }}>
                              {m.sources.knowledge.length > 0 && (
                                <p className="text-[11px] flex items-start gap-1" style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>
                                  <BookOpen size={10} className="mt-0.5 flex-shrink-0" style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
                                  <span>{m.sources.knowledge.map((k) => k.title).filter(Boolean).join(' · ')}</span>
                                </p>
                              )}
                              {m.sources.products.length > 0 && (
                                <p className="text-[11px] flex items-start gap-1" style={{ color: 'rgb(var(--c-text-muted-rgb))' }}>
                                  <Package size={10} className="mt-0.5 flex-shrink-0" style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
                                  <span>{m.sources.products.map((p) => p.name).filter(Boolean).join(' · ')}</span>
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    {pgLoading && (
                      <div className="flex justify-start">
                        <div
                          className="flex items-center gap-1.5 px-3 py-2"
                          style={{ background: 'rgb(var(--c-primary-dark-rgb))', borderRadius: '12px 12px 12px 3px' }}
                        >
                          {[0, 150, 300].map((delay) => (
                            <div
                              key={delay}
                              className="w-1.5 h-1.5 rounded-full animate-bounce"
                              style={{ background: 'rgb(var(--c-accent-green-rgb))', animationDelay: `${delay}ms` }}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {pgError && (
                  <div
                    className="mb-3 rounded-lg px-3 py-2 text-xs"
                    style={{
                      background: 'rgb(var(--c-danger-rgb) / 0.1)',
                      border: '1px solid rgb(var(--c-danger-rgb) / 0.3)',
                      color: 'rgb(var(--c-danger-rgb))',
                    }}
                  >
                    {pgError}
                  </div>
                )}

                <div className="flex gap-2">
                  <input
                    type="text"
                    value={pgInput}
                    onChange={(e) => setPgInput(e.target.value)}
                    onKeyDown={handlePlaygroundKeyDown}
                    disabled={pgLoading}
                    placeholder="Message…"
                    className="flex-1 px-4 py-2.5 rounded-lg text-sm outline-none transition-colors disabled:opacity-50"
                    style={{
                      background: 'rgb(var(--c-primary-dark-rgb))',
                      color: 'rgb(var(--c-text-primary-rgb))',
                      border: '1px solid rgb(var(--c-border-rgb))',
                    }}
                    onFocus={(e) => (e.target.style.borderColor = 'rgb(var(--c-accent-green-rgb))')}
                    onBlur={(e) => (e.target.style.borderColor = 'rgb(var(--c-border-rgb))')}
                  />
                  <button
                    onClick={handlePlaygroundSend}
                    disabled={pgLoading || !pgInput.trim()}
                    className="p-2.5 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ background: 'rgb(var(--c-accent-green-rgb))', color: '#000' }}
                    onMouseEnter={(e) => { if (!pgLoading && pgInput.trim()) e.currentTarget.style.opacity = '0.85'; }}
                    onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                  >
                    {pgLoading
                      ? <div className="w-[18px] h-[18px] rounded-full border-2" style={spinStyleBlack} />
                      : <Send size={17} />
                    }
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
